import OrderIdentifiers from '../OrderIdentifiers';
import React, { useState, useRef } from 'react';
import {
  IconCamera,
  IconAlertTriangle,
  IconInfoCircle,
  IconPrinter,
  IconLock,
} from '@tabler/icons-react';
import { packagesFor } from '../../lib/materialReceive';
import { getPrintOnReceiveDefault } from '../../lib/tenant';
import { useAuth } from '../AuthContext';
import { workOrderRefError, isValidWorkOrderRef, WORK_ORDER_REF_HINT } from '../../lib/workOrderRef';
import OnboardingCallout from '../OnboardingCallout';
import ActionButton from '../ActionButton';
import QtyStepper, { fieldBox } from '../QtyStepper';

// #278. Both reads below used to take material_request_items[0], which is only the
// line being received if it happens to be first. #274's fix makes that true by
// construction on the InventoryActionCatalog.js path, which now passes a
// single-element array - but the InventoryItemDetail.js path hands over the whole
// request with all of its lines, ordered by nothing in particular, so a two-line
// request could show the other item's quantity. Never fired before #212, because
// every request was single-item.
//
// The [0] fallback is deliberate and must stay: material_request_items.item_id is
// nullable by design for off-catalog rows, so a match is impossible there and the
// old behaviour is the correct answer.
function resolveRequestLine(matchedRequest, itemId) {
  const lines = matchedRequest?.material_request_items;
  if (!Array.isArray(lines) || lines.length === 0) return null;
  if (itemId != null) {
    const matched = lines.find(l => l.item_id === itemId);
    if (matched) return matched;
  }
  return lines[0];
}

// #279. What is still owed on this line, not what was originally ordered. Before
// #279 a receive closed the whole request, so a line could never be revisited and
// "outstanding" and "requested" were always the same number. Now that a partial
// receive leaves the request open, the sheet is reachable a second time - and
// defaulting to qty_requested there would re-receive the full order on a Confirm
// tap that looks like a confirmation of the remainder.
//
// Returns null rather than 0 when nothing is outstanding, so the caller falls back
// to 1 and the stepper stays usable: an over-receipt is a real thing a receiver may
// need to record (see submitReceiveDelivery's over-receipt warning), and this sheet
// warns rather than blocks, per the standing rule on qty_on_hand.
function outstandingFor(line) {
  if (!line) return null;
  const requested = parseFloat(line.qty_requested);
  if (!Number.isFinite(requested)) return null;
  const receivedRaw = line.qty_received != null ? parseFloat(line.qty_received) : 0;
  const received = Number.isFinite(receivedRaw) ? receivedRaw : 0;
  const remaining = requested - received;
  return remaining > 0 ? remaining : null;
}

// #333. The sentence under the label stepper, which exists so the receiver can
// see where the number came from and therefore whether to trust it. Gate #333
// callout B: naming the vendor matters because the pack size is a fact about
// THAT vendor's pallet — the per-vendor model surfaced rather than hidden.
//
// Returns the arithmetic as three separate pieces rather than one string so the
// "= N labels" half can be weighted differently, and so the packQty <= 1 case
// reads as a sentence about singles rather than a division by one.
//
// The "= N" half reads the count that will ACTUALLY print, not the derived one.
// Caught in live testing on staging: overriding 2 up to 3 left the sentence
// reading "60 sheet ÷ 50 per pallet = 2 labels" beside a stepper showing 3 — a
// control contradicting its own arithmetic, which is precisely the failure D5
// exists to prevent. When the two differ the derivation is demoted to a
// suggestion rather than dropped, so the receiver can still see where the
// number they overrode came from.
function packArithmetic(qty, uom, packQty, packUnit, labelCopies) {
  const derived = packagesFor(qty, packQty);
  const overridden = labelCopies !== derived;
  const unitWord = uom || 'units';
  const label = `= ${labelCopies} label${labelCopies !== 1 ? 's' : ''}`;
  if (!(packQty > 1)) {
    return {
      left: `${qty} ${unitWord}`,
      middle: overridden ? `· ${derived} suggested` : '· sold singly',
      label,
    };
  }
  return {
    left: `${qty} ${unitWord}`,
    middle: `÷ ${packQty} per ${packUnit || 'pack'}${overridden ? ` suggests ${derived}` : ''}`,
    label,
  };
}

export default function ReceiveSheetContent({
  itemName,
  itemId,
  itemUom,
  branchName,
  matchedRequest,
  canOverride,
  printOnReceiveDefault,
  printDescription,
  printAvailable,
  packQty,          // #333 — vendor_items.pack_qty for this receive's vendor
  packUnit,         // #333 — vendor_items.pack_unit, e.g. "pallet"
  packVendorName,   // #333 — whose pallet this is, for callout B
  onSubmit,
  onCancel,
  saving,
  done,
  orderClosed,
  photoFailed,      // #497 — the receive was saved but its packing slip photo was not
  serverError,
  // #509, Session 79.21c. An off-catalog line has no catalog item and therefore
  // no stock record, so this receive settles the ORDER and moves no count. The
  // sheet has to say so: its footer promised the opposite.
  isOffCatalog = false,
}) {
  const requestLine = resolveRequestLine(matchedRequest, itemId);
  const alreadyReceived = requestLine?.qty_received != null ? parseFloat(requestLine.qty_received) : 0;
  const outstanding = outstandingFor(requestLine);
  const [qty, setQty] = useState(outstanding ?? 1);
  const [jobRef, setJobRef] = useState(matchedRequest?.work_order_ref ?? '');
  const [notes, setNotes] = useState('');
  const [packingSlip, setPackingSlip] = useState(null);
  const [localError, setLocalError] = useState('');
  const [confirmNoSlip, setConfirmNoSlip] = useState(false);
  // #342, Session 78c. This read
  //   useState(printAvailable ? (printOnReceiveDefault ?? true) : false)
  // and was therefore ALWAYS false. Every caller sets printOption to null and
  // resolves the printer asynchronously, so printAvailable is false on the first
  // render, and useState's initialiser only ever runs once — the resolved value
  // could never reach it. The default was unreachable, and the sheet told the
  // receiver to "tap to skip" a toggle that was already off.
  //
  // Same override-rather-than-copy shape as #333's label count, and for the same
  // reason: null means "the receiver has not touched this", so the default stays
  // live until they do. A useEffect syncing printLabels to printAvailable would
  // have re-armed the toggle every time the resolver settled and silently
  // overridden someone who turned it off on purpose.
  const [printToggle, setPrintToggle] = useState(null);
  // #199 part 3, Session 79.22b.6.1f (approved drawing C) -- with QR label
  // printing turned off for the company, this sheet neither offers printing nor
  // prints: the toggle and the label count are not drawn, and printLabels is
  // false, which is the one flag materialReceive.js prints on. Read defensively
  // so a render outside the sign-in provider keeps today's behaviour.
  const labelPrintingEnabled = useAuth()?.labelPrintingEnabled !== false;
  const printLabels = printAvailable && labelPrintingEnabled
    ? (printToggle != null ? printToggle : (printOnReceiveDefault ?? getPrintOnReceiveDefault()))
    : false;
  const packingSlipRef = useRef();
  const pdfSlipRef = useRef();
  // First-run callouts gate, Wave 1 — inv_receive_qty_first.
  //
  // CORRECTED Session 78o, #443. The gate's callout B said "anchor to the label
  // rather than the stepper, because anchoring to the input drops the tooltip
  // over the control it is describing." The intent is right and the mechanism
  // did not achieve it: OnboardingCallout places a 'below' tooltip at
  // anchorRect.bottom + 8, and the label's bottom IS the top of the stepper, so
  // anchoring to the label produced exactly the outcome the decision was made to
  // avoid. Measured live at 1432px: callout top 294, qty input top 294, a 44x80
  // overlap that hid the field completely.
  //
  // The anchor is now the whole field GROUP — label plus stepper row — so
  // "below" means below the control rather than on top of it. This honours the
  // gate's intent; it does not overturn it.
  //
  // Found by writing the Quick Start's receive section, which is the same way
  // #441 was found by writing the privacy policy: a document that has to be true
  // about a screen reads that screen harder than a test does.
  const qtyFieldRef = useRef(null);

  // #333. null means "still following the quantity", which is the state the
  // sheet opens in and stays in until the receiver touches the stepper.
  //
  // Deliberately an override rather than a synced copy of the derived value.
  // A useState seeded from qty would freeze at whatever the quantity was when
  // the sheet mounted, and the pack size arrives asynchronously after open, so
  // the count would be stale twice over — once for every qty change and once
  // more for the fetch that has not landed yet. Reading through a null override
  // makes both cases correct with no effect and no dependency array.
  const [labelOverride, setLabelOverride] = useState(null);

  const parsedQty = parseInt(qty) || 0;
  const derivedCopies = packagesFor(parsedQty, packQty);
  const labelCopies = labelOverride != null ? labelOverride : derivedCopies;
  const arithmetic = packArithmetic(parsedQty, itemUom, packQty, packUnit, labelCopies);

  const error = localError || serverError;

  const upstreamWoRef = matchedRequest?.work_order_ref || null;

  // #502, Session 79.1 — A LOCKED WORK ORDER NUMBER THE DATABASE WILL REFUSE IS A
  // DEAD END, so an invalid one is never locked.
  //
  // What happens without this. `material_requests.work_order_ref` carries NO format
  // rule — checked live on both databases — while `inventory_transactions` does. So
  // a field member can raise a request typing "WO-1234", purchasing can order it,
  // and the delivery then arrives at a receive sheet where that number is copied in
  // and made READ-ONLY for anyone below manager. They press Confirm, the database
  // refuses the row, and the one person standing at the pallet is the one person
  // who cannot correct the field. The delivery can never be received.
  //
  // The lock exists so a receiver does not casually retype a number purchasing set,
  // and that is right for a number that WORKS. It is indefensible for one that
  // cannot be saved by anybody. So the lock applies only while the upstream value
  // is valid; an invalid one opens for editing and says why.
  const upstreamWoInvalid = !!upstreamWoRef && !isValidWorkOrderRef(upstreamWoRef);
  const isLocked = !!upstreamWoRef && !canOverride && !upstreamWoInvalid;
  const isAmber = !!upstreamWoRef && canOverride && !upstreamWoInvalid;

  // #499 — the rule is checked as the person types, so the sheet can refuse before
  // the press instead of the database refusing after it. Empty stays valid: the
  // field is optional and #277 deliberately made it so.
  const jobRefError = workOrderRefError(jobRef);

  function handleSubmit() {
    if (!parsedQty || parsedQty < 1) {
      setLocalError('Please enter a valid quantity.');
      return;
    }
    // #499 — refuse here rather than let Postgres refuse invisibly. Before Session
    // 79.1 a work order typed the way people say it out loud ("WO-1234") produced a
    // 400 the screen never showed: the sheet closed, no stock moved, and the
    // receiver had every reason to believe the delivery was recorded.
    if (jobRefError) {
      setLocalError(jobRefError);
      return;
    }
    setLocalError('');

    // #277: a packing slip photo is the preferred record of what arrived, but it
    // is not required and deliberately cannot be. The gate this replaced was a
    // CHECK constraint satisfiable by one character in any of three fields, so it
    // bought a non-empty string rather than documentation. One confirm tap asks
    // for the photo without letting a receiver be blocked by not having one.
    if (!packingSlip && !confirmNoSlip) {
      setConfirmNoSlip(true);
      return;
    }
    onSubmit({ qty: parsedQty, jobRef: jobRef.trim(), notes: notes.trim(), packingSlip, matchedRequest, printLabels, labelCopies });
  }

  function attachSlip(file) {
    setPackingSlip(file || null);
    if (file) setConfirmNoSlip(false);
  }

  if (done) {
    return (
      <>
        <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', marginBottom: 20, textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
          {/* #509, Session 79.21c -- "Stock received" is not what happened when
              the line has no catalog item. Nothing reached a shelf, because
              there is no shelf. */}
          <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>
            {isOffCatalog ? 'Delivery recorded' : 'Stock received'}
          </div>
          {/* #279. This used to assert "The open order has been closed" on every
              matched receive, which was false the moment a partial receive stopped
              closing the request. orderClosed carries the database's own answer
              (receive_material_request_items()'s all_complete); null means the
              caller could not tell us, and the sheet says nothing rather than
              guessing at what happened to the order.

              #509 EXTENDS THAT EXACT REASONING ONE STEP: "Inventory has been
              updated" is the same kind of confident lie for an off-catalog line,
              and it was still here because nobody could reach this screen with
              one until now. Proven on staging -- the order moved to received and
              inventory_transactions did not gain a row.

              AND THE FIRST VERSION OF THIS REPLACEMENT WAS TRUE AND INCOMPLETE,
              which Colby caught: it read "No stock count changed" and stopped,
              which tells somebody their count went nowhere. It did not.
              promote_off_catalog_item() follows vendor_items.provisional_request_
              item_id back to this line, reads qty_received, and posts a real
              `receive` transaction -- so the catalog entry OPENS at what actually
              arrived. Proven end to end at 79.21c: 1 of 2 received on line 43,
              promoted, and item 60 opened at qty_on_hand 1 with transaction 116.
              THAT CHAIN IS WHAT THIS ISSUE UNLOCKS -- before it, this screen
              could never set qty_received, so promotion read zero. */}
          <div style={{ fontSize: 12, color: '#5C5C5C' }}>
            {isOffCatalog
              ? 'Recorded against this order. Nothing moved on a shelf yet — your count is kept, and it lands as opening stock when someone adds this item to the catalog.'
              : 'Inventory has been updated.'}
            {matchedRequest && orderClosed === true ? ' The open order has been closed.' : ''}
            {matchedRequest && orderClosed === false ? ' The rest of this order is still outstanding.' : ''}
          </div>
        </div>
        {/* #497 — the count is saved and the photo is not, said in that order and in
            those words. The old behaviour threw the whole receive away and showed a
            message naming only the photo, which reads as "the count went in" and is
            the opposite of what had happened. Nothing here asks them to re-count. */}
        {photoFailed && (
          <div style={{ background: '#FAEEDA', border: '1.5px solid #EF9F27', borderRadius: 12, padding: '12px 14px', marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <IconAlertTriangle size={16} color="#633806" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#633806' }}>The packing slip photo did not upload</div>
              <div style={{ fontSize: 11, color: '#633806', marginTop: 2 }}>
                The stock count is saved — do not receive it again. Only the photo is missing
                from this receipt. Keep the paper slip and hand it to your manager.
              </div>
            </div>
          </div>
        )}
        <button onClick={onCancel} style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer' }}>Done</button>
      </>
    );
  }

  return (
    <>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F', marginBottom: 2 }}>Receive stock</div>
      <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 16 }}>{itemName} · {branchName}</div>

      {/* Catalog promotion is deliberately absent from every receive sheet.
          #243 gave promotion its own screen (Off-Catalog Review) because
          creating permanent company-wide master data is master-data
          governance, not a step inside a stock transaction -- the same split
          SAP and Coupa make. 74q removed the equivalent bridge from
          ReceiveDeliverySheetContent.js; this one was removed at 74v. Do not
          reintroduce it here. See ISSUES.md #273. */}

      {matchedRequest && (
        <div style={{ background: '#E6F1FB', border: '1px solid #B5D4F4', borderRadius: 8, padding: '10px 12px', marginBottom: 14, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <IconInfoCircle size={15} color="#0C447C" style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0C447C' }}>
              {alreadyReceived > 0 ? 'Partly received order' : 'Open order found'}
            </div>
            <div style={{ fontSize: 11, color: '#0C447C', marginTop: 1 }}>
              {matchedRequest.request_number} · {requestLine?.qty_requested ?? '—'} requested
            </div>
            {alreadyReceived > 0 && (
              <div style={{ fontSize: 11, fontWeight: 700, color: '#0C447C', marginTop: 1 }}>
                {alreadyReceived} already received · {outstanding ?? 0} still outstanding
              </div>
            )}
          </div>
        </div>
      )}

      {/* #304 D16 / #545, Session 79.14d. THIS SHEET IS WHERE SOMEBODY STANDS AT
          A PALLET deciding whether the boxes in front of them belong to this
          order, and it showed TWO of the six numbers an order carries — one of
          them only when present, so the panel changed shape between deliveries.
          Colby's ruling put the whole receiving pair in scope: "the sheet needs
          all of the numbers tied to the order though."

          No search here and none needed: you arrive having already chosen the
          order on the picker. What was missing was the confirming half. */}
      {matchedRequest && (
        <div style={{ marginBottom: 14 }}>
          <OrderIdentifiers req={matchedRequest} title="This order" />
        </div>
      )}

      {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}

      <div ref={qtyFieldRef}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 8 }}>Qty received</div>
      {/* #760 - the shared stepper (approved drawing, section 1): 40 to a
          finger, 32 to a mouse, the box as it was. Whole numbers only, so a
          typed 2.5 is refused instead of quietly received as 2. */}
      <QtyStepper value={String(qty ?? '')} onChange={setQty} min={1} label="Qty received"
        boxStyle={fieldBox(80, 44, 22)} rowStyle={{ marginBottom: 16 }}>
        {itemUom && <span style={{ fontSize: 12, color: '#9A9A9A' }}>{itemUom}</span>}
      </QtyStepper>
      </div>

      {/* #333, gate decisions D1/D4/D5 and callouts A/B/C. The label count is its
          own control rather than a mode on the quantity field: a "per pallet /
          per sheet" toggle would hide the number, and the number is the thing
          that goes wrong. A stepper reading 1 is unambiguous in a way a selected
          radio button never is, and it covers the case neither mode does — a
          split pallet needing two.

          Never enforced and over-printing is never blocked, same rule as
          qty_on_hand going negative: a receiver who wants fifty labels because
          the pallet is being broken out onto fifty shelves is describing
          physical reality, and Stede's job is to record it rather than argue. */}
      {printAvailable && labelPrintingEnabled && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 8 }}>Labels to print</div>
          {/* #760 - the shared stepper; an emptied box is 0 labels, as before. */}
          <QtyStepper value={String(labelCopies)} min={0} label="Labels to print"
            fewerLabel="One fewer label" moreLabel="One more label"
            onChange={v => setLabelOverride(v === '' ? 0 : parseInt(v, 10))}
            boxStyle={fieldBox(80, 44, 22)} rowStyle={{ marginBottom: 6 }} />

          {/* Callout D — the arithmetic is printed under the control so nobody
              discovers the count was wrong after the tape has run. */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap', fontSize: 12, color: '#7A7A7A', marginBottom: 2 }}>
            <span>{arithmetic.left}</span>
            <span>{arithmetic.middle}</span>
            <span style={{ fontWeight: 700, color: '#085041' }}>{arithmetic.label}</span>
          </div>
          <div style={{ fontSize: 11, color: '#9A9A9A', marginBottom: 16 }}>
            {packQty > 1
              ? `Pack size${packVendorName ? ` from ${packVendorName}` : ''} — change the count if the ${packUnit || 'pack'} was split`
              : 'One label per unit — no pack size on file for this vendor'}
            {labelOverride != null && derivedCopies !== labelOverride ? ' · you set this count' : ''}
          </div>
        </>
      )}

      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>
        WO# <span style={{ fontWeight: 400, textTransform: 'none', color: '#9A9A9A' }}>(optional)</span>
      </div>
      <input
        type="text"
        value={jobRef}
        onChange={e => !isLocked && setJobRef(e.target.value)}
        readOnly={isLocked}
        placeholder="e.g. 123456 or 123456-7"
        style={{
          width: '100%', height: 40, borderRadius: 10, padding: '0 12px',
          fontSize: 16, color: isLocked ? '#7A7A7A' : '#0F0F0F',
          background: isLocked ? '#F5F5F3' : isAmber ? '#FAEEDA' : 'white',
          border: jobRefError ? '1.5px solid #A32D2D' : isAmber ? '1.5px solid #EF9F27' : '1px solid #E0E0E0',
          marginBottom: 4, boxSizing: 'border-box',
        }}
      />
      {/* #499 — said beside the field the person is typing in, not at the top of a
          sheet they have already scrolled past. */}
      {jobRefError && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, fontSize: 11, color: '#A32D2D', marginBottom: 16 }}>
          <IconAlertTriangle size={11} color="#A32D2D" style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{WORK_ORDER_REF_HINT}</span>
        </div>
      )}
      {/* #502 — the number purchasing set cannot be saved, so it has been opened for
          editing. Without this the receiver is locked out of the only field standing
          between them and recording the delivery. */}
      {upstreamWoInvalid && !jobRefError && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, fontSize: 11, color: '#9A5F00', marginBottom: 16 }}>
          <IconAlertTriangle size={11} color="#BA7517" style={{ flexShrink: 0, marginTop: 1 }} />
          <span>The work order number on this order ("{upstreamWoRef}") is not a valid one, so you can correct it here.</span>
        </div>
      )}
      {isLocked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#7A7A7A', marginBottom: 16 }}>
          <IconLock size={11} />
          Set upstream — read only for your role
        </div>
      )}
      {isAmber && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#9A5F00', marginBottom: 16 }}>
          <IconAlertTriangle size={11} color="#BA7517" />
          Overriding locked value — change will be logged
        </div>
      )}
      {!isLocked && !isAmber && !jobRefError && !upstreamWoInvalid && <div style={{ marginBottom: 16 }} />}

      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>
        Notes <span style={{ fontWeight: 400, textTransform: 'none', color: '#9A9A9A' }}>(optional)</span>
      </div>
      <input
        type="text"
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="e.g. partial shipment, 2 boxes damaged"
        style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 10, padding: '0 12px', fontSize: 16, color: '#0F0F0F', marginBottom: 16, boxSizing: 'border-box' }}
      />

      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>
        Packing slip <span style={{ fontWeight: 400, textTransform: 'none', color: '#9A9A9A' }}>(optional)</span>
      </div>
      <input ref={packingSlipRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => attachSlip(e.target.files[0])} />
      <input ref={pdfSlipRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={e => attachSlip(e.target.files[0])} />
      <button onClick={() => packingSlipRef.current.click()} style={{ width: '100%', padding: '12px 0', border: '1px dashed #C0C0C0', borderRadius: 10, background: '#F8F8F8', fontSize: 13, color: '#5C5C5C', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 6 }}>
        <IconCamera size={16} color="#7A7A7A" />
        Take photo
      </button>
      <button onClick={() => pdfSlipRef.current.click()} style={{ width: '100%', background: 'none', border: 'none', fontSize: 11, color: '#9A9A9A', cursor: 'pointer', padding: '2px 0', marginBottom: 4, textDecoration: 'underline' }}>
        or attach a PDF
      </button>
      {packingSlip && (
        <div style={{ fontSize: 11, color: '#5C5C5C', marginBottom: 8, textAlign: 'center' }}>
          Attached: {packingSlip.name}
        </div>
      )}
      {!packingSlip && !confirmNoSlip && (
        <div style={{ background: '#FAEEDA', border: '1px solid #EF9F27', borderRadius: 8, padding: '8px 12px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconAlertTriangle size={14} color="#633806" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 11, color: '#633806' }}>No packing slip attached — a photo is the preferred record</span>
        </div>
      )}

      {labelPrintingEnabled && (
      <button
        onClick={() => printAvailable && setPrintToggle(!printLabels)}
        disabled={!printAvailable}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${!printAvailable ? '#E0E0E0' : printLabels ? '#9FE1CB' : '#E0E0E0'}`, background: !printAvailable ? '#F5F5F3' : printLabels ? '#E8F8F2' : '#F8F8F8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 16, cursor: printAvailable ? 'pointer' : 'not-allowed', opacity: printAvailable ? 1 : 0.6 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconPrinter size={16} color={!printAvailable ? '#C0C0C0' : printLabels ? '#1D9E75' : '#9A9A9A'} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: !printAvailable ? '#9A9A9A' : printLabels ? '#085041' : '#7A7A7A' }}>
              {/* #333 D7. This string used to read the received QUANTITY, which
                  is the defect's most visible surface — "Print 50 labels" for a
                  single pallet. It now reads the same labelCopies the print call
                  uses, so the promise on the toggle and the job that dispatches
                  cannot disagree. */}
              {!printAvailable ? 'Printing unavailable' : printLabels ? `Print ${labelCopies} label${labelCopies !== 1 ? 's' : ''} after confirming` : 'Print labels after confirming'}
            </div>
            <div style={{ fontSize: 11, color: !printAvailable ? '#9A9A9A' : printLabels ? '#0F6E56' : '#9A9A9A' }}>
              {printAvailable ? (printLabels ? printDescription : 'Tap to enable') : printDescription}
            </div>
          </div>
        </div>
        <div style={{ width: 36, height: 22, borderRadius: 11, background: !printAvailable ? '#E0E0E0' : printLabels ? '#1D9E75' : '#D0D0D0', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
          <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: printAvailable && printLabels ? 16 : 2, transition: 'left 0.2s' }} />
        </div>
      </button>
      )}

      <div style={{ height: 4 }} />

      {confirmNoSlip && !packingSlip && (
        <div style={{ background: '#FAEEDA', border: '1.5px solid #EF9F27', borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
            <IconAlertTriangle size={16} color="#633806" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#633806' }}>Receive without a packing slip photo?</div>
              <div style={{ fontSize: 11, color: '#633806', marginTop: 2 }}>
                A photo is the preferred record of what actually arrived. You can still receive without one.
              </div>
            </div>
          </div>
          <button
            onClick={() => packingSlipRef.current.click()}
            style={{ width: '100%', padding: 12, borderRadius: 10, border: 'none', background: '#EF9F27', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}
          >
            <IconCamera size={16} color="white" />
            Take the photo
          </button>
          {/* #499/#503 — blocked, not dead. See components/ActionButton.js: the
              control stays reachable, announces WHY, and answers a press instead
              of swallowing it. The reason is already on screen beside the WO#
              field, so it is not printed a second time here. */}
          <ActionButton
            onClick={handleSubmit}
            blockedReason={jobRefError}
            reasonAlreadyShown
            busy={saving}
            busyLabel="Saving..."
            style={{ width: '100%', background: 'none', border: 'none', fontSize: 12, color: '#633806', cursor: 'pointer', padding: 4, textDecoration: 'underline' }}
          >
            Receive without it
          </ActionButton>
        </div>
      )}

      {!(confirmNoSlip && !packingSlip) && (
        <ActionButton
          onClick={handleSubmit}
          blockedReason={jobRefError}
          reasonAlreadyShown
          busy={saving}
          busyLabel="Saving..."
          style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer', marginBottom: 10 }}
        >
          {jobRefError ? 'Check the WO# above' : 'Confirm receipt'}
        </ActionButton>
      )}
      <button onClick={onCancel} style={{ width: '100%', background: 'none', border: 'none', fontSize: 13, color: '#7A7A7A', cursor: 'pointer', padding: 4 }}>Cancel</button>
      {/* #509, Session 79.21c -- THIS LINE WAS FALSE FOR AN OFF-CATALOG RECEIVE
          AND IT WAS FOUND BY OPENING THE SHEET, not by a test. There is no
          inventory_stock row for a line with no catalog item, submitReceiveDelivery
          posts no inventory transaction for one, and saying otherwise tells the
          person at the pallet to expect a number that will never move. */}
      <div style={{ fontSize: 11, color: '#9A9A9A', textAlign: 'center', marginTop: 8 }}>
        {isOffCatalog
          ? 'Records the delivery against this order. Your count is kept and lands on the shelf when this item is added to the catalog.'
          : 'Updates stock count immediately'}
      </div>

      {/* First-run callouts gate, Wave 1 — inv_receive_qty_first, and gate
          decision D4 in its most concrete form. The condition is not merely
          "a matched request": outstandingFor() returns null when nothing is
          still owed, and the stepper then falls back to 1. Firing on
          matchedRequest alone would put "this is what is still owed" beside a
          1 that came from nowhere — a tip describing something not on screen,
          which is exactly what D4 forbids. */}
      <OnboardingCallout
        calloutKey="inv_receive_qty_first"
        text="This is what is still owed on the order. Change it to what actually turned up."
        anchorRef={qtyFieldRef}
        show={!!matchedRequest && outstanding != null}
      />
    </>
  );
}